import clsx from 'clsx';
import { FunctionComponent, RefObject, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import ClickAwayListener from 'react-click-away-listener';
import { useMediaQuery } from 'react-responsive';
import MobileBottomModal from '../MobileBottomModal';
import styles from './ThreeDotsDropdownList.module.scss';
import ThreeDotsDropdownListItem from './ThreeDotsDropdownListItem';
import { ThreeDotsDropdownOption } from './ThreeDotsDropdownOption';

interface ThreeDotsDropdownListProps {
    options: Array<ThreeDotsDropdownOption>;
    className?: string;
    direction: 'up' | 'down';
    onClose: () => void;
    usePortal?: boolean;
    triggerRef?: RefObject<HTMLElement>;
}

export const ThreeDotsDropdownList: FunctionComponent<ThreeDotsDropdownListProps> = props => {
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });

    const down = props.direction === 'down';

    const [portalStyle, setPortalStyle] = useState<React.CSSProperties>();

    useLayoutEffect(() => {
        if (!props.usePortal || !props.triggerRef?.current) return;

        const rect = props.triggerRef.current.getBoundingClientRect();

        setPortalStyle(
            down
                ? { position: 'fixed', top: rect.bottom + 4, right: window.innerWidth - rect.right }
                : {
                      position: 'fixed',
                      bottom: window.innerHeight - rect.top + 4,
                      right: window.innerWidth - rect.right
                  }
        );

        // The menu is positioned once on open; rather than tracking scroll to
        // keep it glued to the trigger, just close it so it never renders stale.
        const closeOnScroll = () => props.onClose();
        window.addEventListener('scroll', closeOnScroll, true);
        return () => window.removeEventListener('scroll', closeOnScroll, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.usePortal, props.triggerRef, down]);

    const clickHandler = (option: ThreeDotsDropdownOption) => {
        option.onClick();
        props.onClose();
    };

    const items = useMemo(
        () =>
            props.options.map(option => (
                <ThreeDotsDropdownListItem
                    key={option.id}
                    label={option.label}
                    icon={option.icon}
                    disabled={option.disabled}
                    onClick={() => clickHandler(option)}
                    className={styles.item}
                />
            )),
        [props.options]
    );

    if (isMobile) {
        return <MobileBottomModal onClickAway={props.onClose}>{items}</MobileBottomModal>;
    }

    const content = (
        <ClickAwayListener onClickAway={props.onClose}>
            <div
                className={clsx(styles.container, { [styles.down]: down, [styles.up]: !down }, props.className)}
                style={props.usePortal ? portalStyle : undefined}
            >
                {items}
            </div>
        </ClickAwayListener>
    );

    return props.usePortal ? createPortal(content, document.body) : content;
};

export default ThreeDotsDropdownList;
